import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import NumberedPagination from "../../../shared/ui/Pagination/NumberedPagination";
import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Checkbox from "../../../shared/ui/Field/Checkbox";
import InlineError from "../../../shared/ui/Field/InlineError";
import { messageFromError } from "../../../shared/api/drfErrors";
import {
  fetchCompany,
  fetchMembers,
  fetchMyCompany,
} from "../../companyWorkspace/api/companyWorkspace";
import { isAdminMembership } from "../../companyWorkspace/lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../../companyWorkspace/lib/scopeSession";
import { useCompanyFocusRevalidation } from "../../companyWorkspace/lib/companyFocusRevalidation";
import CompanyPageHeader from "../../companyWorkspace/ui/CompanyPageHeader";
import { isCorporatePropertiesAvailable } from "../lib/propertyLabels";
import {
  activeMemberOptions,
  allowedValuesFor,
  canonicalChoiceText,
  classificationLabel,
  classificationTone,
  committableCounts,
  buildMediaMapping,
  createIdempotencyKey,
  diagnosticCodeText,
  diagnosticText,
  explicitMappingFromSuggestions,
  fieldKindLabel,
  formatLabel,
  importFieldLabel,
  missingMappingText,
  fileContentToken,
  importRequestFingerprint,
  inspectWarningText,
  isInterruptedOperation,
  matchCodeText,
  mediaCountsText,
  mediaFailureLabel,
  mediaKindLabel,
  mediaStatusText,
  missingRequiredMappings,
  operationStateTone,
  outcomeTone,
  presentApiError,
  textError,
  operationStateLabel,
  outcomeLabel,
  readOperationId,
  requirementsClaimBlocked,
  resolveCommitKey,
  sheetSelectionRequired,
  sourceNamespacePrompt,
  unresolvedHeaders,
} from "../lib/propertyImportPresentation";
import {
  commitPropertyImport,
  fetchPropertyImportMedia,
  fetchPropertyImportOperation,
  fetchPropertyImportOperationRows,
  inspectPropertyImport,
  previewPropertyImport,
} from "../api/propertyImport";

const ROW_PAGE_SIZE = 20;
const MEDIA_POLL_MS = 3000;
const ROW_RESULTS_UNAVAILABLE =
  "Імпорт прийнято. Результати рядків не завантажились. Оновіть сторінку, щоб відкрити збережену операцію.";
const INTERRUPTED_SAME_ACTOR_COPY =
  "Операцію перервано. Вона не завершена. Щоб продовжити, виберіть той самий файл і повторно підтвердьте параметри імпорту. Уже оброблені рядки не будуть створені повторно.";
const CROSS_ACTOR_RESUME_COPY =
  "Операцію перервано. Продовжити цей імпорт може лише користувач, який його розпочав.";
const RELOAD_RESUME_COPY =
  "Оригінальний файл не зберігається. Оберіть його повторно та підтвердьте ті самі параметри. Продовження використає ключ початкової операції.";
const SESSION_INTERRUPTED_COPY =
  "Операцію перервано. Вона не завершена. Той самий запит можна надіслати знову. Уже оброблені рядки не будуть створені повторно.";
const INTERRUPTED_INCOMPLETE_COPY =
  "Операцію перервано. Вона не завершена. Результат не є успішним імпортом.";

function durableOperationId(payload) {
  const value = Number(payload && payload.id);
  if (!Number.isInteger(value) || value <= 0) return null;
  return value;
}

function errorView(err) {
  const data = err && err.response && err.response.data;
  const code = data && typeof data === "object" && data.code ? String(data.code) : "";
  return {
    status: err && err.response ? err.response.status : null,
    code,
    detail: messageFromError(err),
  };
}

function integerUserMap(mapping) {
  const next = {};
  Object.keys(mapping || {}).forEach((key) => {
    const userId = Number(mapping[key]);
    if (Number.isInteger(userId) && userId > 0) next[key] = userId;
  });
  return next;
}

function toggleOrderedColumn(current, header, enabled) {
  const next = (Array.isArray(current) ? current : []).filter((item) => item !== header);
  if (enabled) next.push(header);
  return next;
}

function CompanyPropertyImportContent() {
  const { companyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyRef = useRef(false);
  const commitKeyRef = useRef(null);
  const pinnedResumeKeyRef = useRef(null);

  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const fileInputRef = useRef(null);
  const stepsRef = useRef(null);
  const [fileToken, setFileToken] = useState("");
  const [inspect, setInspect] = useState(null);
  const [sheet, setSheet] = useState("");
  const [sheetExplicit, setSheetExplicit] = useState(false);
  const [mapping, setMapping] = useState({});
  const [mappingConfirmed, setMappingConfirmed] = useState(false);
  const [valueMappings, setValueMappings] = useState({});
  const [responsibleMapping, setResponsibleMapping] = useState({});
  const [sourceNamespace, setSourceNamespace] = useState("");
  const [imageColumns, setImageColumns] = useState([]);
  const [planColumns, setPlanColumns] = useState([]);
  const [mediaSeparator, setMediaSeparator] = useState("");
  const [mediaPage, setMediaPage] = useState(null);
  const [members, setMembers] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewFingerprint, setPreviewFingerprint] = useState("");
  const [operation, setOperation] = useState(null);
  const [rowPage, setRowPage] = useState(null);
  const [commitAttempt, setCommitAttempt] = useState(null);
  const [resumeMode, setResumeMode] = useState(null);
  const [busy, setBusy] = useState(false);

  const companyMatches = company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const authorityMatches = companyMatches && actorMatches;
  const canImport =
    authorityMatches &&
    isAdminMembership(company, membership) &&
    isCorporatePropertiesAvailable(company);

  const operationId = readOperationId(location.search);
  const fields = inspect?.canonical_fields || [];
  const mediaMapping = useMemo(
    () => buildMediaMapping({
      imageColumns,
      planColumns,
      separator: mediaSeparator,
    }),
    [imageColumns, planColumns, mediaSeparator],
  );
  const fingerprint = useMemo(
    () =>
      importRequestFingerprint({
        fileToken,
        sheet,
        mapping,
        valueMappings,
        responsibleMapping,
        sourceNamespace,
        mediaMapping,
      }),
    [
      fileToken,
      sheet,
      mapping,
      valueMappings,
      responsibleMapping,
      sourceNamespace,
      mediaMapping,
    ],
  );
  const previewMatches = Boolean(preview) && previewFingerprint === fingerprint;
  const counts = committableCounts(preview?.summary);
  const namespaceNeeded = sourceNamespacePrompt(mapping);
  const sheetRequired = sheetSelectionRequired(inspect);
  const openHeaders = unresolvedHeaders(inspect?.headers, mapping, [
    ...imageColumns,
    ...planColumns,
  ]);
  const mediaOpenCount =
    (Number(operation?.media?.pending) || 0) +
    (Number(operation?.media?.processing) || 0);
  const requirements = preview?.mapping_requirements;
  const requirementsTruncated = requirementsClaimBlocked(requirements);
  const actorCanResume =
    operation != null &&
    isInterruptedOperation(operation.state) &&
    sameActorId(operation.actor_id, actorId) &&
    Boolean(operation.idempotency_key);
  const sameSessionContinuation =
    resumeMode === "session" &&
    Boolean(file) &&
    operation != null &&
    isInterruptedOperation(operation.state) &&
    (operation.actor_id == null || sameActorId(operation.actor_id, actorId));

  const clearProtected = useCallback(() => {
    setInspect(null);
    setPreview(null);
    setPreviewFingerprint("");
    setOperation(null);
    setRowPage(null);
    setMembers([]);
    setMapping({});
    setValueMappings({});
    setResponsibleMapping({});
    setImageColumns([]);
    setPlanColumns([]);
    setMediaSeparator("");
    setMediaPage(null);
    setMappingConfirmed(false);
    setCommitAttempt(null);
    setFile(null);
    setFileToken("");
    setSheet("");
    setSheetExplicit(false);
    setSourceNamespace("");
    setResumeMode(null);
    commitKeyRef.current = null;
    pinnedResumeKeyRef.current = null;
  }, []);

  const loadOperation = useCallback(
    async (op, id) => {
      const payload = await fetchPropertyImportOperation(companyId, id);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      setOperation(payload);
      setStep(5);
      setState("ready");
      try {
        const rows = await fetchPropertyImportOperationRows(companyId, id, {
          page: 1,
          page_size: ROW_PAGE_SIZE,
        });
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
        setRowPage(rows);
      } catch (rowErr) {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
        const view = errorView(rowErr);
        if (view.status === 403 || view.status === 404) {
          throw rowErr;
        }
        setRowPage(null);
        setError(textError(ROW_RESULTS_UNAVAILABLE));
        return;
      }
      try {
        const media = await fetchPropertyImportMedia(companyId, id, {
          page: 1,
          page_size: ROW_PAGE_SIZE,
          state: "failed",
        });
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
        setMediaPage(media);
      } catch (mediaErr) {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
        const view = errorView(mediaErr);
        if (view.status === 403 || view.status === 404) {
          throw mediaErr;
        }
        setMediaPage(null);
      }
    },
    [companyId, scopeGate],
  );

  const loadAuthority = useCallback(async ({ background = false } = {}) => {
    if (!companyId) {
      setState("not-found");
      return;
    }
    const op = scopeGate.beginRead({ companyId, actorId });
    if (!background) {
      setState("loading");
      setError(null);
    }
    try {
      const [companyData, mine] = await Promise.all([
        fetchCompany(companyId),
        fetchMyCompany(),
      ]);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      const mineCompanyMatches =
        mine?.company?.id != null && sameCompanyId(mine.company.id, companyId);
      const mineMatches = mineCompanyMatches ? mine.membership : null;
      setCompany(companyData);
      setMembership(mineMatches);
      setAuthorityActorId(actorId);
      const allowed =
        mineCompanyMatches &&
        isAdminMembership(companyData, mineMatches) &&
        isCorporatePropertiesAvailable(companyData);
      if (!allowed) {
        clearProtected();
        setState(mineCompanyMatches ? "denied" : "denied");
        return;
      }
      const id = readOperationId(location.search);
      if (id) {
        await loadOperation(op, id);
        return;
      }
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      setState("ready");
    } catch (err) {
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      clearProtected();
      const view = errorView(err);
      if (view.status === 404) setState("not-found");
      else if (view.status === 403) setState("denied");
      else {
        setError(presentApiError(view));
        setState("error");
      }
    }
  }, [actorId, clearProtected, companyId, loadOperation, location.search, scopeGate]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({ companyId, actorId });
    busyRef.current = false;
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    setFile(null);
    setFileToken("");
    setSheet("");
    setSheetExplicit(false);
    setSourceNamespace("");
    setStep(1);
    clearProtected();
    loadAuthority();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyRef.current = false;
    };
  }, [companyId, actorId]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready" && canImport,
    busy,
    onRevalidate: () => loadAuthority({ background: true }),
  });

  useLayoutEffect(() => {
    const list = stepsRef.current;
    if (!list) return undefined;
    const align = () => {
      const current = list.querySelector('[aria-current="step"]');
      if (!current) return;
      const listRect = list.getBoundingClientRect();
      const itemRect = current.getBoundingClientRect();
      if (itemRect.left < listRect.left - 1) {
        list.scrollLeft -= listRect.left - itemRect.left;
      } else if (itemRect.right > listRect.right + 1) {
        list.scrollLeft += itemRect.right - listRect.right;
      }
    };
    align();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(align);
    observer.observe(list);
    return () => observer.disconnect();
  }, [step, state]);

  useEffect(() => {
    if (!canImport || !mapping.responsible_source) {
      setMembers([]);
      return undefined;
    }
    const op = scopeGate.beginRead({ companyId, actorId });
    let cancelled = false;
    fetchMembers(companyId, "active")
      .then((payload) => {
        if (cancelled || !scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        setMembers(activeMemberOptions(payload));
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [actorId, canImport, companyId, mapping.responsible_source, scopeGate]);

  useEffect(() => {
    if (step !== 5 || !operation || mediaOpenCount < 1 || !canImport) return undefined;
    const op = scopeGate.beginRead({ companyId, actorId });
    const timer = setInterval(() => {
      if (busyRef.current) return;
      const id = operation.id;
      Promise.all([
        fetchPropertyImportOperation(companyId, id),
        fetchPropertyImportOperationRows(companyId, id, {
          page: rowPage && rowPage.page ? rowPage.page : 1,
          page_size: ROW_PAGE_SIZE,
        }),
        fetchPropertyImportMedia(companyId, id, {
          page: 1,
          page_size: ROW_PAGE_SIZE,
          state: "failed",
        }),
      ]).then(([payload, rows, media]) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
        setOperation(payload);
        setRowPage(rows);
        setMediaPage(media);
      }).catch((err) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
        const view = errorView(err);
        if (view.status === 403 || view.status === 404) {
          clearProtected();
          setState(view.status === 404 ? "not-found" : "denied");
        }
      });
    }, MEDIA_POLL_MS);
    return () => clearInterval(timer);
  }, [
    actorId,
    canImport,
    clearProtected,
    companyId,
    mediaOpenCount,
    operation,
    rowPage,
    scopeGate,
    step,
  ]);

  function invalidatePreview() {
    setMappingConfirmed(false);
    setPreview(null);
    setPreviewFingerprint("");
    commitKeyRef.current = null;
  }

  function updateMediaColumns(kind, header, enabled) {
    if (kind === "image") {
      setImageColumns((current) => toggleOrderedColumn(current, header, enabled));
      if (enabled) {
        setPlanColumns((current) => current.filter((item) => item !== header));
      }
    } else {
      setPlanColumns((current) => toggleOrderedColumn(current, header, enabled));
      if (enabled) {
        setImageColumns((current) => current.filter((item) => item !== header));
      }
    }
    invalidatePreview();
  }

  async function onSelectFile(nextFile) {
    setError(null);
    setCommitAttempt(null);
    setFile(nextFile);
    setInspect(null);
    setPreview(null);
    setPreviewFingerprint("");
    setMapping({});
    setMappingConfirmed(false);
    setValueMappings({});
    setResponsibleMapping({});
    setImageColumns([]);
    setPlanColumns([]);
    setMediaSeparator("");
    setMediaPage(null);
    setSheet("");
    setSheetExplicit(false);
    setOperation(null);
    setRowPage(null);
    commitKeyRef.current = null;
    if (!nextFile) {
      setFileToken("");
      return;
    }
    const token = await fileContentToken(nextFile);
    if (!mountedRef.current) return;
    setFileToken(token);
    await runInspect(nextFile, "");
  }

  async function runInspect(nextFile, nextSheet) {
    const op = scopeGate.beginRead({ companyId, actorId });
    setBusy(true);
    busyRef.current = true;
    setError(null);
    try {
      const payload = await inspectPropertyImport(companyId, {
        file: nextFile,
        sheet: nextSheet || undefined,
      });
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      setInspect(payload);
      setStep(1);
    } catch (err) {
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      const view = errorView(err);
      if (view.status === 403 || view.status === 404) {
        clearProtected();
        setState(view.status === 404 ? "not-found" : "denied");
        return;
      }
      setError(presentApiError(view));
    } finally {
      if (mountedRef.current) {
        setBusy(false);
        busyRef.current = false;
      }
    }
  }

  async function onSheetChange(nextSheet) {
    if (!nextSheet) {
      setSheet("");
      setSheetExplicit(false);
      setPreview(null);
      setPreviewFingerprint("");
      setMappingConfirmed(false);
      commitKeyRef.current = null;
      return;
    }
    setSheet(nextSheet);
    setSheetExplicit(true);
    setPreview(null);
    setPreviewFingerprint("");
    setMappingConfirmed(false);
    commitKeyRef.current = null;
    if (file) await runInspect(file, nextSheet);
  }

  function applySuggestions() {
    const suggested = explicitMappingFromSuggestions(inspect?.header_suggestions);
    setMapping(suggested);
    setMappingConfirmed(false);
    setPreview(null);
    setPreviewFingerprint("");
    commitKeyRef.current = null;
  }

  function updateMapping(key, header) {
    setMapping((current) => {
      const next = { ...current };
      if (!header) delete next[key];
      else next[key] = header;
      return next;
    });
    setMappingConfirmed(false);
    setPreview(null);
    setPreviewFingerprint("");
    commitKeyRef.current = null;
  }

  async function runPreview() {
    if (!file || busyRef.current) return null;
    const missing = missingRequiredMappings(fields, mapping);
    if (missing.length) {
      setError(textError(missingMappingText(fields, missing)));
      return null;
    }
    const op = scopeGate.beginRead({ companyId, actorId });
    setBusy(true);
    busyRef.current = true;
    setError(null);
    const requestFingerprint = importRequestFingerprint({
      fileToken,
      sheet,
      mapping,
      valueMappings,
      responsibleMapping,
      sourceNamespace,
      mediaMapping,
    });
    try {
      const payload = await previewPropertyImport(companyId, {
        file,
        sheet: sheet || undefined,
        mapping,
        valueMappings,
        responsibleMapping: integerUserMap(responsibleMapping),
        sourceNamespace,
        mediaMapping,
      });
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return null;
      setPreview(payload);
      setPreviewFingerprint(requestFingerprint);
      setCommitAttempt(null);
      return payload;
    } catch (err) {
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return null;
      const view = errorView(err);
      if (view.status === 403 || view.status === 404) {
        clearProtected();
        setState(view.status === 404 ? "not-found" : "denied");
        return null;
      }
      setError(presentApiError(view));
      return null;
    } finally {
      if (mountedRef.current) {
        setBusy(false);
        busyRef.current = false;
      }
    }
  }

  async function confirmMapping() {
    const missing = missingRequiredMappings(fields, mapping);
    if (missing.length) {
      setError(textError(missingMappingText(fields, missing)));
      return;
    }
    setMappingConfirmed(true);
    setError(null);
    setStep(3);
    await runPreview();
  }

  function updateValueMapping(field, token, canonical) {
    setValueMappings((current) => {
      const nextField = { ...(current[field] || {}) };
      if (!canonical) delete nextField[token];
      else nextField[token] = canonical;
      const next = { ...current };
      if (Object.keys(nextField).length) next[field] = nextField;
      else delete next[field];
      return next;
    });
    setPreviewFingerprint("");
    commitKeyRef.current = null;
  }

  function updateResponsible(token, userId) {
    setResponsibleMapping((current) => {
      const next = { ...current };
      if (!userId) delete next[token];
      else next[token] = Number(userId);
      return next;
    });
    setPreviewFingerprint("");
    commitKeyRef.current = null;
  }

  function clearSourceDraft() {
    setFile(null);
    setFileToken("");
    setInspect(null);
    setPreview(null);
    setPreviewFingerprint("");
    setMapping({});
    setMappingConfirmed(false);
    setValueMappings({});
    setResponsibleMapping({});
    setImageColumns([]);
    setPlanColumns([]);
    setMediaSeparator("");
    setMediaPage(null);
    setSourceNamespace("");
    setSheet("");
    setSheetExplicit(false);
    setRowPage(null);
    setCommitAttempt(null);
    commitKeyRef.current = null;
  }

  function beginReloadedResume() {
    if (
      !operation ||
      !isInterruptedOperation(operation.state) ||
      !sameActorId(operation.actor_id, actorId) ||
      !operation.idempotency_key
    ) {
      return;
    }
    pinnedResumeKeyRef.current = operation.idempotency_key;
    setResumeMode("reload");
    setError(null);
    setOperation(null);
    clearSourceDraft();
    setFileInputKey((current) => current + 1);
    setStep(1);
    navigate(
      { pathname: `/companies/${companyId}/properties/import` },
      { replace: true },
    );
  }

  function exitToNewImport() {
    pinnedResumeKeyRef.current = null;
    setResumeMode(null);
    setOperation(null);
    setError(null);
    clearSourceDraft();
    setFileInputKey((current) => current + 1);
    setStep(1);
    navigate(
      { pathname: `/companies/${companyId}/properties/import` },
      { replace: true },
    );
  }

  async function commit() {
    if (busyRef.current || !file || !previewMatches) return;
    if (counts.attempted < 1) return;
    const op = scopeGate.beginRead({ companyId, actorId });
    const keyState = pinnedResumeKeyRef.current
      ? { key: pinnedResumeKeyRef.current, fingerprint }
      : resolveCommitKey({
          previous: commitKeyRef.current,
          fingerprint,
          createKey: createIdempotencyKey,
        });
    commitKeyRef.current = keyState;
    setBusy(true);
    busyRef.current = true;
    setError(null);
    setCommitAttempt(null);
    try {
      const payload = await commitPropertyImport(companyId, {
        file,
        sheet: sheet || undefined,
        mapping,
        valueMappings,
        responsibleMapping: integerUserMap(responsibleMapping),
        sourceNamespace,
        mediaMapping,
        idempotencyKey: keyState.key,
      });
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      const durableId = durableOperationId(payload);
      if (durableId == null) {
        setCommitAttempt("failed");
        setError(textError("Відповідь коміту не містить ідентифікатора операції."));
        return;
      }
      setOperation({ ...payload, id: durableId });
      setCommitAttempt("settled");
      if (isInterruptedOperation(payload.state)) {
        pinnedResumeKeyRef.current = keyState.key;
        setResumeMode("session");
      } else {
        pinnedResumeKeyRef.current = null;
        setResumeMode(null);
      }
      setStep(5);
      navigate(
        {
          pathname: `/companies/${companyId}/properties/import`,
          search: `?operation=${durableId}`,
        },
        { replace: true },
      );
      try {
        const rows = await fetchPropertyImportOperationRows(companyId, durableId, {
          page: 1,
          page_size: ROW_PAGE_SIZE,
        });
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
        setRowPage(rows);
        try {
          const media = await fetchPropertyImportMedia(companyId, durableId, {
            page: 1,
            page_size: ROW_PAGE_SIZE,
            state: "failed",
          });
          if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
          setMediaPage(media);
        } catch (mediaErr) {
          if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
          const mediaView = errorView(mediaErr);
          if (mediaView.status === 403 || mediaView.status === 404) {
            clearProtected();
            setState(mediaView.status === 404 ? "not-found" : "denied");
            return;
          }
          setMediaPage(null);
        }
      } catch (rowErr) {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
        const view = errorView(rowErr);
        if (view.status === 403 || view.status === 404) {
          clearProtected();
          setState(view.status === 404 ? "not-found" : "denied");
          return;
        }
        setRowPage(null);
        setError(textError(ROW_RESULTS_UNAVAILABLE));
      }
    } catch (err) {
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      const view = errorView(err);
      if (view.status === 409 && view.code === "idempotency_conflict") {
        setCommitAttempt("conflict");
        setError(presentApiError(view));
        return;
      }
      if (view.status === 403 || view.status === 404) {
        clearProtected();
        setState(view.status === 404 ? "not-found" : "denied");
        return;
      }
      if (view.status == null || view.status >= 500) {
        setCommitAttempt(view.status == null ? "uncertain" : "failed");
        setError(
          view.status == null
            ? textError(
                "Відповідь не отримано. Той самий ключ ідемпотентності збережено для повтору.",
              )
            : presentApiError(view),
        );
        return;
      }
      setCommitAttempt("failed");
      setError(presentApiError(view));
    } finally {
      if (mountedRef.current) {
        setBusy(false);
        busyRef.current = false;
      }
    }
  }

  async function changeRowPage(nextPage) {
    if (!operation || busyRef.current) return;
    const op = scopeGate.beginRead({ companyId, actorId });
    setBusy(true);
    busyRef.current = true;
    try {
      const rows = await fetchPropertyImportOperationRows(companyId, operation.id, {
        page: nextPage,
        page_size: ROW_PAGE_SIZE,
      });
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      setRowPage(rows);
    } catch (err) {
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      const view = errorView(err);
      if (view.status === 403 || view.status === 404) {
        clearProtected();
        setState(view.status === 404 ? "not-found" : "denied");
        return;
      }
      setError(presentApiError(view));
    } finally {
      if (mountedRef.current) {
        setBusy(false);
        busyRef.current = false;
      }
    }
  }

  const stepTitle = [
    "Файл",
    "Колонки",
    "Значення",
    "Попередній перегляд",
    "Результат",
  ];

  return (
    <section
      className="clients-page feature-company-workspace feature-company-properties feature-company-property-import"
      id="Csection"
      tabIndex="-1"
      data-step={step}
      aria-busy={busy ? "true" : "false"}
    >
      <div className="container">
        <CompanyPageHeader
          title="Імпорт об'єктів"
          subtitle="Об'єкти компанії · Імпорт"
          company={authorityMatches ? company : null}
          membership={authorityMatches ? membership : null}
          companyId={companyId}
          activeNav="properties"
          actions={
            canImport ? (
              <Link
                to={`/companies/${companyId}/properties`}
                className="action-btn action-btn--ghost"
              >
                До списку
              </Link>
            ) : null
          }
        />

        {state === "loading" ? (
          <div className="feature-company-workspace__state">
            <Spinner />
          </div>
        ) : null}

        {state === "denied" ? (
          <EmptyState
            title="Немає доступу"
            description="Імпорт доступний лише активному власнику або керівнику компанії."
          />
        ) : null}

        {state === "not-found" ? (
          <EmptyState
            title="Не знайдено"
            description="Компанію або операцію імпорту не знайдено."
          />
        ) : null}

        {state === "error" ? (
          <EmptyState
            title="Помилка завантаження"
            description={(error && error.primary) || "Спробуйте оновити сторінку."}
            primaryAction={
              <button type="button" className="action-btn" onClick={loadAuthority}>
                Повторити
              </button>
            }
          />
        ) : null}

        {state === "ready" && canImport ? (
          <div className="clients-workspace">
            <ol
              ref={stepsRef}
              className="feature-company-property-import__steps"
              aria-label="Кроки імпорту"
            >
              {stepTitle.map((label, index) => (
                <li
                  key={label}
                  aria-current={step === index + 1 ? "step" : undefined}
                >
                  {index + 1}. {label}
                </li>
              ))}
            </ol>

            {error ? (
              <div className="feature-company-property-import__error">
                <InlineError>{error.primary}</InlineError>
              </div>
            ) : null}

            {resumeMode === "reload" ? (
              <div className="feature-company-property-import__panel">
                <p role="status">{RELOAD_RESUME_COPY}</p>
                <button
                  type="button"
                  className="action-btn action-btn--ghost"
                  disabled={busy}
                  onClick={exitToNewImport}
                >
                  Новий імпорт
                </button>
              </div>
            ) : null}

            {step === 1 ? (
              <div className="feature-company-property-import__panel">
                <div className="feature-company-property-import__file">
                  <label className="ui-field__label" htmlFor="property-import-file">
                    Файл CSV або XLSX
                  </label>
                  <input
                    key={fileInputKey}
                    ref={fileInputRef}
                    id="property-import-file"
                    className="feature-company-property-import__file-input"
                    type="file"
                    accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    aria-label="Файл CSV або XLSX"
                    disabled={busy}
                    onChange={(event) => {
                      const next = event.target.files && event.target.files[0];
                      onSelectFile(next || null);
                    }}
                  />
                  <div className="feature-company-property-import__file-actions">
                    <button
                      type="button"
                      className="action-btn action-btn--ghost"
                      disabled={busy}
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    >
                      {file ? "Замінити файл" : "Обрати файл"}
                    </button>
                  </div>
                  <p className="feature-company-workspace__meta" role="status">
                    {file ? file.name : "Файл не обрано"}
                  </p>
                </div>
                <p className="feature-company-workspace__meta">
                  Файл читає сервер. Браузер не розбирає XLSX самостійно.
                  Формули не виконуються.
                </p>
                {inspect?.bounds ? (
                  <p className="feature-company-workspace__meta">
                    Обмеження сервера: {inspect.bounds.max_upload_bytes} байт,{" "}
                    {inspect.bounds.max_data_rows} рядків, {inspect.bounds.max_columns}{" "}
                    колонок, {inspect.bounds.max_cell_length} символів у комірці,{" "}
                    {inspect.bounds.max_sheets} аркушів.
                  </p>
                ) : null}
                {inspect ? (
                  <div>
                    <p>
                      Формат: {formatLabel(inspect.format)}. Рядків: {inspect.row_count}. Колонок:{" "}
                      {(inspect.headers || []).length}.
                    </p>
                    {(inspect.warnings || []).map((warning) => {
                      const presented = inspectWarningText(warning);
                      return (
                        <p
                          key={`${warning.code}-${warning.detail || ""}`}
                          className="feature-company-property-import__status-block"
                          role="status"
                        >
                          <span>{presented.primary}</span>
                        </p>
                      );
                    })}
                    {sheetRequired ? (
                      <Field id="property-import-sheet" label="Аркуш">
                        <select
                          className="ui-select"
                          aria-label="Аркуш"
                          value={sheetExplicit ? sheet : ""}
                          disabled={busy}
                          onChange={(event) => onSheetChange(event.target.value)}
                        >
                          <option value="">Оберіть аркуш</option>
                          {(inspect.sheets || []).map((item) => (
                            <option key={item.name} value={item.name}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </Field>
                    ) : null}
                    {sheetRequired && !sheetExplicit ? (
                      <p role="status">Оберіть аркуш явно. Перший аркуш не підтверджено.</p>
                    ) : null}
                    <button
                      type="button"
                      className="action-btn"
                      disabled={busy || (sheetRequired && !sheetExplicit)}
                      onClick={() => setStep(2)}
                    >
                      Далі
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {step === 2 ? (
              <div className="feature-company-property-import__panel">
                <p>
                  Пропозиції сервера не є зіставленням. Підтвердіть колонки явно.
                </p>
                <button
                  type="button"
                  className="action-btn action-btn--ghost"
                  disabled={busy}
                  onClick={applySuggestions}
                >
                  Застосувати однозначні пропозиції
                </button>
                <div className="feature-company-property-import__map-list">
                  {fields.map((field) => (
                    <Field
                      key={field.key}
                      id={`property-import-map-${field.key}`}
                      className="feature-company-property-import__map-row"
                      label={`${importFieldLabel(field)}${field.required_mapping ? " (обов'язково)" : ""}`}
                      help={fieldKindLabel(field.kind) || undefined}
                    >
                      <select
                        className="ui-select"
                        aria-label={`Колонка для ${importFieldLabel(field)}`}
                        value={mapping[field.key] || ""}
                        disabled={busy}
                        onChange={(event) => updateMapping(field.key, event.target.value)}
                      >
                        <option value="">Не зіставлено</option>
                        {(inspect?.headers || []).map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ))}
                </div>
                <fieldset>
                  <legend>Колонки фото</legend>
                  <p className="feature-company-workspace__meta">
                    Порядок вибору є порядком фото. Сервер не вгадує колонки.
                  </p>
                  <div className="feature-company-property-import__choice-list">
                    {(inspect?.headers || []).map((header, index) => (
                      <Checkbox
                        key={`image-${header}`}
                        id={`property-import-image-${index}`}
                        label={header}
                        aria-label={`Фото ${header}`}
                        checked={imageColumns.includes(header)}
                        disabled={busy}
                        onChange={(event) => updateMediaColumns("image", header, event.target.checked)}
                      />
                    ))}
                  </div>
                  {imageColumns.length ? (
                    <p>Порядок фото: {imageColumns.join(", ")}</p>
                  ) : (
                    <p>Колонки фото не обрано.</p>
                  )}
                </fieldset>
                <fieldset>
                  <legend>Колонки планів</legend>
                  <div className="feature-company-property-import__choice-list">
                    {(inspect?.headers || []).map((header, index) => (
                      <Checkbox
                        key={`plan-${header}`}
                        id={`property-import-plan-${index}`}
                        label={header}
                        aria-label={`План ${header}`}
                        checked={planColumns.includes(header)}
                        disabled={busy}
                        onChange={(event) => updateMediaColumns("plan", header, event.target.checked)}
                      />
                    ))}
                  </div>
                  {planColumns.length ? (
                    <p>Порядок планів: {planColumns.join(", ")}</p>
                  ) : (
                    <p>Колонки планів не обрано.</p>
                  )}
                </fieldset>
                <Field
                  id="property-import-media-separator"
                  label="Роздільник кількох посилань"
                  help="Порожній роздільник означає одне посилання в комірці. Браузер не завантажує медіа."
                >
                  <TextInput
                    aria-label="Роздільник медіа"
                    value={mediaSeparator}
                    disabled={busy}
                    onChange={(event) => {
                      setMediaSeparator(event.target.value);
                      invalidatePreview();
                    }}
                  />
                </Field>
                <div aria-label="Незіставлені колонки">
                  {openHeaders.length ? (
                    <ul>
                      {openHeaders.map((header) => {
                        const suggestion = (inspect?.header_suggestions || []).find(
                          (item) => item.header === header,
                        );
                        return (
                          <li key={header}>
                            {header} —{" "}
                            {suggestion?.status === "ambiguous"
                              ? "неоднозначно"
                              : "не зіставлено"}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p>Усі колонки файлу зіставлено.</p>
                  )}
                </div>
                <button
                  type="button"
                  className="action-btn action-btn--ghost"
                  disabled={busy}
                  onClick={() => setStep(1)}
                >
                  Назад
                </button>
                <button
                  type="button"
                  className="action-btn"
                  disabled={busy}
                  onClick={confirmMapping}
                >
                  Підтвердити зіставлення
                </button>
              </div>
            ) : null}

            {step === 3 && mappingConfirmed ? (
              <div className="feature-company-property-import__panel">
                <p>
                  Канонічні значення беруться з метаданих сервера. Незіставлене
                  значення блокує лише свій рядок.
                </p>
                {requirementsTruncated ? (
                  <p role="status">
                    Список неповний. Не можна вважати, що всі значення зіставлено.
                  </p>
                ) : null}
                {(requirements?.fields || []).length === 0 && previewMatches && !requirementsTruncated ? (
                  <p>Незіставлених значень немає.</p>
                ) : null}
                {(requirements?.fields || []).map((requirement) => (
                  <fieldset key={requirement.field}>
                    <legend>
                      {importFieldLabel({ key: requirement.field })}
                      {fieldKindLabel(requirement.kind)
                        ? ` · ${fieldKindLabel(requirement.kind)}`
                        : ""}
                      {requirement.truncated
                        ? ` · пропущено ${requirement.omitted_count}`
                        : ""}
                    </legend>
                    {requirement.source_values.map((token, index) =>
                      requirement.kind === "responsible" ? (
                        <Field
                          key={token}
                          id={`property-import-responsible-${requirement.field}-${index}`}
                          className="feature-company-property-import__map-row"
                          label={token}
                        >
                          <select
                            className="ui-select"
                            aria-label={`Відповідальний для ${token}`}
                            value={responsibleMapping[token] || ""}
                            disabled={busy}
                            onChange={(event) =>
                              updateResponsible(token, event.target.value)
                            }
                          >
                            <option value="">Не зіставлено</option>
                            {members.map((member) => (
                              <option key={member.userId} value={member.userId}>
                                {member.label}
                              </option>
                            ))}
                          </select>
                        </Field>
                      ) : (
                        <Field
                          key={token}
                          id={`property-import-value-${requirement.field}-${index}`}
                          className="feature-company-property-import__map-row"
                          label={token}
                        >
                          <select
                            className="ui-select"
                            aria-label={`Канонічне значення для ${importFieldLabel({ key: requirement.field })} ${token}`}
                            value={
                              (valueMappings[requirement.field] || {})[token] || ""
                            }
                            disabled={busy}
                            onChange={(event) =>
                              updateValueMapping(
                                requirement.field,
                                token,
                                event.target.value,
                              )
                            }
                          >
                            <option value="">Не зіставлено</option>
                            {allowedValuesFor(fields, requirement.field).map((value) => (
                              <option key={value} value={value}>
                                {canonicalChoiceText(value, requirement.field)}
                              </option>
                            ))}
                          </select>
                        </Field>
                      ),
                    )}
                  </fieldset>
                ))}
                {namespaceNeeded ? (
                  <Field
                    id="property-import-namespace"
                    label="Простір джерела"
                    help="Простір джерела ідентифікує зовнішню систему, а не ім'я файлу. Довжину і нормалізацію визначає сервер (максимум 64 символи). Без нього рядки із зовнішнім ідентифікатором залишаються заблокованими. Існуюче джерело не оновлює об'єкт."
                  >
                    <TextInput
                      aria-label="Простір джерела"
                      value={sourceNamespace}
                      disabled={busy}
                      onChange={(event) => {
                        setSourceNamespace(event.target.value);
                        setPreviewFingerprint("");
                        commitKeyRef.current = null;
                      }}
                    />
                  </Field>
                ) : null}
                <p className="feature-company-workspace__meta">
                  Робочий статус імпорту не є публікацією в каталозі. Імпортовані
                  об'єкти залишаються неопублікованими. Проміжні переходи до
                  статусу «Активне» для «Зарезервовано» або «Продано» не
                  показуються як історія джерела.
                </p>
                <button
                  type="button"
                  className="action-btn action-btn--ghost"
                  disabled={busy}
                  onClick={() => setStep(2)}
                >
                  Назад
                </button>
                <button
                  type="button"
                  className="action-btn"
                  disabled={busy}
                  onClick={async () => {
                    const payload = await runPreview();
                    if (payload) setStep(4);
                  }}
                >
                  Попередній перегляд
                </button>
              </div>
            ) : null}

            {step === 4 ? (
              <div className="feature-company-property-import__panel">
                {!previewMatches ? (
                  <p role="status">
                    Вхідні дані змінено. Потрібен новий попередній перегляд перед імпортом.
                  </p>
                ) : (
                  <>
                    <p>
                      Готово: {preview.summary.ready}. Готово з попередженням:{" "}
                      {preview.summary.warning}. Помилка: {preview.summary.error}.
                      Можливий дублікат: {preview.summary.possible_duplicate}.
                      {preview.summary.truncated
                        ? " Показано не всі рядки файлу."
                        : ""}
                    </p>
                    <p>
                      Буде спроба імпорту: {counts.attempted}. Заблоковано і не
                      створюється: {counts.blocked}.
                    </p>
                    <p>
                      {`Медіа до обробки: ${preview.summary.media?.work_item_count || 0}. Попередній перегляд не завантажує файли.`}
                    </p>
                    <p>
                      Робочий статус імпорту не є публікацією в каталозі.
                    </p>
                    {requirementsTruncated ? (
                      <p role="status">
                        Список неповний. Не можна вважати, що всі значення зіставлено.
                      </p>
                    ) : null}
                    <PreviewRows companyId={companyId} rows={preview.rows || []} />
                  </>
                )}
                <button
                  type="button"
                  className="action-btn action-btn--ghost"
                  disabled={busy}
                  onClick={() => setStep(3)}
                >
                  Назад
                </button>
                <button
                  type="button"
                  className="action-btn"
                  disabled={busy || !previewMatches || counts.attempted < 1}
                  onClick={commit}
                >
                  {busy ? "Імпорт виконується" : "Імпортувати"}
                </button>
                {commitAttempt === "uncertain" ? (
                  <button
                    type="button"
                    className="action-btn"
                    disabled={busy}
                    onClick={commit}
                  >
                    Повторити той самий запит
                  </button>
                ) : null}
              </div>
            ) : null}

            {step === 5 && operation ? (
              <div className="feature-company-property-import__panel">
                <div className="feature-company-property-import__status-block">
                  <PresentedStatus
                    label={operationStateLabel(operation.state)}
                    code={operation.state}
                    tone={operationStateTone(operation.state)}
                  />
                </div>
                {sameSessionContinuation ? (
                  <p role="status">{SESSION_INTERRUPTED_COPY}</p>
                ) : null}
                {isInterruptedOperation(operation.state) &&
                actorCanResume &&
                resumeMode !== "session" ? (
                  <p role="status">{INTERRUPTED_SAME_ACTOR_COPY}</p>
                ) : null}
                {isInterruptedOperation(operation.state) &&
                !actorCanResume &&
                !sameSessionContinuation ? (
                  <p role="status">
                    {sameActorId(operation.actor_id, actorId)
                      ? INTERRUPTED_INCOMPLETE_COPY
                      : CROSS_ACTOR_RESUME_COPY}
                  </p>
                ) : null}
                {operation.state === "failed" ? (
                  <div className="feature-company-property-import__error">
                    <InlineError>
                      Збій операції. Рядки не позначено як успішно імпортовані.
                    </InlineError>
                    {operation.failure_code ? (
                      <p className="feature-company-workspace__meta">
                        {diagnosticCodeText(operation.failure_code).primary}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {operation.summary ? (
                  <p>
                    Створено: {operation.summary.created}. Заблоковано:{" "}
                    {operation.summary.blocked}. Існуюче джерело:{" "}
                    {operation.summary.existing_source}. Помилки рядків:{" "}
                    {operation.summary.failed}.
                  </p>
                ) : null}
                <p aria-live="polite">{mediaStatusText(operation.media)}</p>
                <FailedMediaList page={mediaPage} />
                <ResultRows companyId={companyId} page={rowPage} />
                {rowPage ? (
                  <NumberedPagination
                    currentPage={rowPage.page}
                    totalPages={Math.max(
                      1,
                      Math.ceil((rowPage.count || 0) / (rowPage.page_size || ROW_PAGE_SIZE)),
                    )}
                    hasNext={
                      rowPage.page * (rowPage.page_size || ROW_PAGE_SIZE) <
                      (rowPage.count || 0)
                    }
                    hasPrevious={rowPage.page > 1}
                    ariaLabel="Сторінки результатів імпорту"
                    disabled={busy}
                    onPageChange={changeRowPage}
                  />
                ) : null}
                {!operationId ? null : (
                  <p className="feature-company-workspace__meta">
                    Операція {operation.id} збережена на цій сторінці.
                  </p>
                )}
                {sameSessionContinuation ? (
                  <button
                    type="button"
                    className="action-btn"
                    disabled={busy || !previewMatches}
                    onClick={commit}
                  >
                    Продовжити цей імпорт
                  </button>
                ) : null}
                {actorCanResume && resumeMode !== "session" ? (
                  <button
                    type="button"
                    className="action-btn"
                    disabled={busy}
                    onClick={beginReloadedResume}
                  >
                    Відновити імпорт
                  </button>
                ) : null}
                <button
                  type="button"
                  className="action-btn action-btn--ghost"
                  disabled={busy}
                  onClick={exitToNewImport}
                >
                  Новий імпорт
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function PresentedStatus({ label, tone }) {
  return (
    <span className="feature-company-property-import__status">
      <span className={`feature-company-property-import__pill feature-company-property-import__pill--${tone || "neutral"}`}>
        {label}
      </span>
    </span>
  );
}

function PreviewRows({ companyId, rows }) {
  return (
    <div className="clients-table-wrap" role="region" aria-label="Рядки попереднього перегляду">
      <table className="clients-table">
        <thead>
          <tr>
            <th scope="col">Рядок</th>
            <th scope="col">Класифікація</th>
            <th scope="col">Діагностика</th>
            <th scope="col">Дублікати</th>
            <th scope="col">Медіа</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.source_row_number}>
              <td data-label="Рядок">{row.source_row_number}</td>
              <td data-label="Класифікація">
                <PresentedStatus
                  label={classificationLabel(row.classification)}
                  code={row.classification}
                  tone={classificationTone(row.classification)}
                />
              </td>
              <td data-label="Діагностика">
                {(row.diagnostics || []).length === 0
                  ? "—"
                  : (row.diagnostics || []).map((item) => {
                      const presented = diagnosticText(item);
                      return (
                        <span
                          key={`${item.code}-${item.field || ""}-${item.message}`}
                          className="feature-company-property-import__status"
                        >
                          <span>{presented.primary}</span>
                        </span>
                      );
                    })}
              </td>
              <td data-label="Дублікати">
                {(row.duplicate_candidates || []).length === 0
                  ? "—"
                  : (row.duplicate_candidates || []).map((candidate) => (
                      <span key={candidate.property_id}>
                        <Link
                          to={`/companies/${companyId}/properties/${candidate.property_id}`}
                        >
                          {candidate.title || `Об'єкт #${candidate.property_id}`}
                        </Link>
                        {" "}
                        {(candidate.match_codes || []).map((code) => (
                          <span key={code}>{matchCodeText(code) || "Збіг"}</span>
                        ))}
                      </span>
                    ))}
              </td>
              <td data-label="Медіа">{mediaCountsText(row.media)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResultRows({ companyId, page }) {
  const rows = page?.results || [];
  return (
    <div className="clients-table-wrap" role="region" aria-label="Результати імпорту">
      <table className="clients-table">
        <thead>
          <tr>
            <th scope="col">Рядок</th>
            <th scope="col">Результат</th>
            <th scope="col">Діагностика</th>
            <th scope="col">Медіа</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.source_row_number}>
              <td data-label="Рядок">{row.source_row_number}</td>
              <td data-label="Результат">
                <PresentedStatus
                  label={outcomeLabel(row.outcome)}
                  code={row.outcome}
                  tone={outcomeTone(row.outcome)}
                />
                {row.outcome === "created" && row.property_id ? (
                  <>
                    {" "}
                    <Link to={`/companies/${companyId}/properties/${row.property_id}`}>
                      Відкрити об'єкт
                    </Link>
                  </>
                ) : null}
                {row.outcome === "existing_source" ? (
                  <span> Оновлення не виконано і дублікат не створено.</span>
                ) : null}
              </td>
              <td data-label="Діагностика">
                {(row.diagnostic_codes || []).length
                  ? (row.diagnostic_codes || []).map((code) => {
                      const presented = diagnosticCodeText(code);
                      return (
                        <span key={code} className="feature-company-property-import__status">
                          <span>{presented.primary}</span>
                        </span>
                      );
                    })
                  : "—"}
              </td>
              <td data-label="Медіа">{mediaStatusText(row.media)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FailedMediaList({ page }) {
  const rows = page?.results || [];
  if (!rows.length) return null;
  return (
    <ul aria-label="Помилки медіа">
      {rows.map((item) => (
        <li key={item.id}>
          Рядок {item.source_row_number}. {mediaKindLabel(item.kind)} {item.source_ordinal}.{" "}
          {item.host || "—"}.{" "}
          <span className="feature-company-property-import__status">
            <span>{mediaFailureLabel(item.failure_code) || "Помилка медіа"}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export default CompanyPropertyImportContent;
